/**
 * 1.7.7 — the month's calendar, reminders armed once, and volunteers who opt in (D-1.7.7-0…3).
 *
 * Pure rules shared by the API and the app; nothing here reads or writes a list.
 *
 *   The calendar   an event has a kind and a place from two short lists (Settings → Events and RSVPs), what to
 *                  bring, an accessibility line, RSVP on or off, and how many volunteers it needs. A Repeat makes
 *                  real events (each its own row, editable or cancelled alone) up to an end date. Copy last month
 *                  proposes this month's events from last month's kinds and weekdays — it never saves.
 *   Reminders      armed by a person's save: one scheduled Outbox row a reminder time, visible and cancellable
 *                  until it goes. When, to whom and whether at all are Settings; an event may differ.
 *   Volunteers     "Volunteers needed" is the event's Volunteer places (1.7.4). A volunteer's "I'm available" is
 *                  their yes on the event's own line; the cap and the waiting list are 1.7.4's.
 *
 * What has no column lives in the event's own file (`_app/events/<EventID>.json`, under `cal`).
 */
import { z } from 'zod';
import { addDaysToKey, dayOfWeek, isDateKey } from './dates';
import type { StandbyView } from './event-standby';
import { EVENT_KINDS_1025, type EventKind1025 } from './event-kind-1025';

/* ------------------------------------------------------------------------------------------------
 * Settings (SM_Settings rows; Admin → Settings → Events and RSVPs)
 * ---------------------------------------------------------------------------------------------- */

export const EVENT_KINDS = 'EVENT_KINDS';
export const EVENT_ROOMS = 'EVENT_ROOMS';
export const EVENT_REMINDERS = 'EVENT_REMINDERS';
export const EVENT_REMINDER_TIMES = 'EVENT_REMINDER_TIMES';
export const EVENT_REMINDER_AUDIENCE = 'EVENT_REMINDER_AUDIENCE';

export const EVENT_KINDS_DEFAULT = 'Bingo; Music; Movie night; Tech discussion; Nail spa; Outing; Meeting; Other';
export const EVENT_ROOMS_DEFAULT = 'Auditorium; Boardroom; Lobby; Community room';
/** Two days before at 10:00 AM, and the day of at 9:00 AM. */
export const EVENT_REMINDER_TIMES_DEFAULT = '2@10:00; 0@09:00';
export const OTHER_KIND = 'Other';
export const MAX_REMINDER_TIMES = 3;
export const MAX_DAYS_BEFORE = 14;

/** A short list kept as one line ("Bingo; Music; …"): trimmed, no blanks, no doubles, at most 30 of 60 characters. */
export function parseList(value: string | null | undefined, fallback: string): string[] {
  const read = (v: string) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of v.split(/[;\n]/)) {
      const x = raw.trim().replace(/\s+/g, ' ').slice(0, 60);
      if (!x || seen.has(x.toLowerCase())) continue;
      seen.add(x.toLowerCase());
      out.push(x);
    }
    return out.slice(0, 30);
  };
  const list = read(String(value ?? ''));
  return list.length ? list : read(fallback);
}
export const validList = (v: string) => v.length <= 1500 && parseList(v, '').length >= 1;

/** One reminder time: so many days before the event (0 = the day of), at this time of day. */
export interface ReminderTime {
  daysBefore: number;
  /** Minutes after midnight, New York. */
  minutes: number;
}

const TIME = /^(\d{1,2})@(\d{1,2}):(\d{2})$/;

/** "2@10:00; 0@09:00" → the times, earliest first; anything unreadable is dropped. At most three. */
export function parseReminderTimes(value: string | null | undefined): ReminderTime[] {
  const out: ReminderTime[] = [];
  for (const raw of String(value ?? '').split(';')) {
    const m = TIME.exec(raw.trim());
    if (!m) continue;
    const daysBefore = Number(m[1]);
    const minutes = Number(m[2]) * 60 + Number(m[3]);
    if (daysBefore > MAX_DAYS_BEFORE || Number(m[2]) > 23 || Number(m[3]) > 59) continue;
    if (out.some((t) => t.daysBefore === daysBefore && t.minutes === minutes)) continue;
    out.push({ daysBefore, minutes });
  }
  return out.sort((a, b) => b.daysBefore - a.daysBefore || a.minutes - b.minutes).slice(0, MAX_REMINDER_TIMES);
}
export const formatReminderTimes = (times: readonly ReminderTime[]) =>
  times
    .slice(0, MAX_REMINDER_TIMES)
    .map((t) => `${t.daysBefore}@${String(Math.floor(t.minutes / 60)).padStart(2, '0')}:${String(t.minutes % 60).padStart(2, '0')}`)
    .join('; ');
/** What Settings takes: one to three readable times and nothing else on the line. */
export const validReminderTimes = (v: string) => {
  const parts = v.split(';').map((s) => s.trim()).filter(Boolean);
  return parts.length >= 1 && parts.length <= MAX_REMINDER_TIMES && parseReminderTimes(v).length === parts.length;
};

export const REMINDER_AUDIENCES = ['all', 'yes', 'nobody'] as const;
/** all = every tenant with an email · yes = only those who said yes (an event with RSVP) · nobody. */
export type ReminderAudience = (typeof REMINDER_AUDIENCES)[number];
export const REMINDER_AUDIENCE_SETTING: Record<ReminderAudience, string> = { all: 'ALL', yes: 'YES', nobody: 'NOBODY' };
export const reminderAudienceOf = (v: string | null | undefined): ReminderAudience => (String(v ?? '').toUpperCase() === 'YES' ? 'yes' : String(v ?? '').toUpperCase() === 'NOBODY' ? 'nobody' : 'all');

export interface ReminderSettings {
  on: boolean;
  times: ReminderTime[];
  audience: ReminderAudience;
}

export function reminderSettingsOf(values: { get(key: string): string | undefined }): ReminderSettings {
  const times = parseReminderTimes(values.get(EVENT_REMINDER_TIMES));
  return { on: String(values.get(EVENT_REMINDERS) ?? 'Yes').trim() !== 'No', times: times.length ? times : parseReminderTimes(EVENT_REMINDER_TIMES_DEFAULT), audience: reminderAudienceOf(values.get(EVENT_REMINDER_AUDIENCE)) };
}

const ReminderTimeSchema = z.object({ daysBefore: z.number().int().min(0).max(MAX_DAYS_BEFORE), minutes: z.number().int().min(0).max(23 * 60 + 59) });

/** "Reminders for this event: default / off / custom". */
export const ReminderChoiceSchema = z
  .object({
    mode: z.enum(['default', 'off', 'custom']),
    times: z.array(ReminderTimeSchema).max(MAX_REMINDER_TIMES).optional(),
    audience: z.enum(REMINDER_AUDIENCES).optional(),
  })
  .refine((c) => c.mode !== 'custom' || (c.times?.length ?? 0) >= 1, { message: 'required', path: ['times'] });
export type ReminderChoice = z.output<typeof ReminderChoiceSchema>;
export const DEFAULT_REMINDER_CHOICE: ReminderChoice = { mode: 'default' };

/**
 * What an event's reminders are, from Settings and its own choice. Settings off is the kill switch: nothing is
 * armed, whatever the event says. "Only those who said yes" on an event with no RSVP has nobody to mean, so there it
 * is everyone.
 */
export function effectiveReminders(settings: ReminderSettings, choice: ReminderChoice | null | undefined, hasRsvp: boolean): ReminderSettings {
  const c = choice ?? DEFAULT_REMINDER_CHOICE;
  if (!settings.on || c.mode === 'off') return { on: false, times: [], audience: 'nobody' };
  const times = c.mode === 'custom' && c.times?.length ? [...c.times].sort((a, b) => b.daysBefore - a.daysBefore || a.minutes - b.minutes).slice(0, MAX_REMINDER_TIMES) : settings.times;
  let audience = c.mode === 'custom' && c.audience ? c.audience : settings.audience;
  if (audience === 'yes' && !hasRsvp) audience = 'all';
  return { on: true, times, audience };
}

/** The day a reminder leaves: so many days before the event's day. (The instant is the server's: New York time.) */
export const reminderDayKey = (eventDayKey: string, t: ReminderTime) => addDaysToKey(eventDayKey, -t.daysBefore);

/* ------------------------------------------------------------------------------------------------
 * Repeat
 * ---------------------------------------------------------------------------------------------- */

/**
 * What Repeat offers (1.7.9, D-1.7.9-1): none · every week · every n weeks (2, 3 or 4 — "every other week" is 2) ·
 * every month on the nth weekday (the 2nd Tuesday; 1st–4th — there is no "5th": it silently skipped months) · every
 * month on the last weekday (the last Thursday, whether the month has four or five).
 */
export const REPEATS = ['none', 'weekly', 'everyN', 'monthlyNth', 'monthlyLast'] as const;
/** What is taken on input: the offered ones, and 1.7.7's `biweekly` (every 2 weeks) so nothing saved or sent breaks. */
export const REPEATS_TAKEN = [...REPEATS, 'biweekly'] as const;
export type RepeatEvery = (typeof REPEATS_TAKEN)[number];
/** "Every n weeks": the n's offered. */
export const REPEAT_WEEKS = [2, 3, 4] as const;
export const MAX_SERIES = 60;

const dayKeySchema = z.string().refine(isDateKey, 'date');
export const RepeatSchema = z
  .object({ every: z.enum(REPEATS_TAKEN), n: z.number().int().min(2).max(4).optional(), until: dayKeySchema.nullable().optional().default(null) })
  .refine((r) => r.every === 'none' || !!r.until, { message: 'required', path: ['until'] })
  // "every other week" is every 2 weeks; the number of weeks is kept only where it means something
  .transform((r): { every: RepeatEvery; n?: number; until: string | null } => (r.every === 'biweekly' || r.every === 'everyN' ? { every: 'everyN', n: r.every === 'biweekly' ? 2 : (r.n ?? 2), until: r.until } : { every: r.every, until: r.until }));
export type RepeatInput = z.input<typeof RepeatSchema>;

/** "The 2nd Tuesday": which one of its weekday in the month a day is (1–5). */
export const nthOfMonth = (dayKey: string) => Math.floor((Number(dayKey.slice(6, 8)) - 1) / 7) + 1;

/** The nth of a weekday (0 = Sunday) in a month (`yyyyMM`); null when the month has no such day (a 5th Tuesday). */
export function nthWeekdayIn(month: string, n: number, weekday: number): string | null {
  const first = `${month}01`;
  const offset = (weekday - dayOfWeek(first) + 7) % 7;
  const day = 1 + offset + (n - 1) * 7;
  const key = `${month}${String(day).padStart(2, '0')}`;
  return isDateKey(key) ? key : null;
}

export const monthOf = (dayKey: string) => dayKey.slice(0, 6);
export function addMonths(month: string, delta: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(4, 6)) - 1 + delta;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  return `${yy}${String(mm + 1).padStart(2, '0')}`;
}
export const isMonth = (s: string) => /^\d{6}$/.test(s) && isDateKey(`${s}01`);
export function daysInMonth(month: string): number {
  for (let d = 31; d >= 28; d--) if (isDateKey(`${month}${String(d).padStart(2, '0')}`)) return d;
  return 28;
}

/** The last of a weekday (0 = Sunday) in a month (`yyyyMM`) — the 4th or the 5th, whichever the month ends with. */
export function lastWeekdayIn(month: string, weekday: number): string {
  const last = daysInMonth(month);
  const day = last - ((dayOfWeek(`${month}${String(last).padStart(2, '0')}`) - weekday + 7) % 7);
  return `${month}${String(day).padStart(2, '0')}`;
}
/** Is this day the last of its weekday in its month (the last Thursday)? */
export const isLastOfMonth = (dayKey: string) => isDateKey(dayKey) && Number(dayKey.slice(6, 8)) + 7 > daysInMonth(monthOf(dayKey));

/** The weeks between two events of "every n weeks" (2 when nothing readable is given). */
export const repeatWeeks = (every: RepeatEvery, n?: number | null) => (every === 'weekly' ? 1 : every === 'biweekly' ? 2 : Math.min(4, Math.max(2, Math.floor(Number(n) || 2))));

/**
 * Why a Repeat cannot start on this day, or null: "the 5th Tuesday" is not a repeat (most months have none — say
 * "the last" there), and "the last Thursday" has to start on one.
 */
export function repeatProblem(startKey: string, every: RepeatEvery): 'fifth' | 'not-last' | null {
  if (!isDateKey(startKey)) return null;
  if (every === 'monthlyNth' && nthOfMonth(startKey) > 4) return 'fifth';
  if (every === 'monthlyLast' && !isLastOfMonth(startKey)) return 'not-last';
  return null;
}
/** What Repeat offers for an event on this day: no "5th", and "the last" only on a last one. */
export const repeatChoices = (startKey: string): RepeatEvery[] => REPEATS.filter((r) => !repeatProblem(startKey, r));

/**
 * Every day of a repeat, the first one included, up to and including the end date — at most sixty. `n` is the weeks
 * of "every n weeks". "The last Thursday" is the last one of each month, the 4th or the 5th. (A 5th weekday is not
 * offered and the save refuses it; asked for here, a month with no such day is skipped, never moved to another week.)
 */
export function repeatDays(startKey: string, every: RepeatEvery, until: string | null, n?: number | null): string[] {
  if (every === 'none' || !until || until < startKey) return [startKey];
  const out = [startKey];
  if (every === 'weekly' || every === 'biweekly' || every === 'everyN') {
    const step = 7 * repeatWeeks(every, n);
    for (let k = addDaysToKey(startKey, step); k <= until && out.length < MAX_SERIES; k = addDaysToKey(k, step)) out.push(k);
    return out;
  }
  const nth = nthOfMonth(startKey);
  const weekday = dayOfWeek(startKey);
  for (let m = addMonths(monthOf(startKey), 1), i = 0; i < 72 && out.length < MAX_SERIES; m = addMonths(m, 1), i++) {
    const k = every === 'monthlyLast' ? lastWeekdayIn(m, weekday) : nthWeekdayIn(m, nth, weekday);
    if (k && k > until) break;
    if (!k && `${m}01` > until) break;
    if (k) out.push(k);
  }
  return out;
}

/** The rule a series was made by, kept in each of its events' files (`cal.repeat`) so a later series edit knows it. */
export interface SeriesRule {
  every: Exclude<RepeatEvery, 'none' | 'biweekly'>;
  /** every n weeks: the n. */
  n?: number;
  /** monthly on the nth weekday: which one (1–4). */
  nth?: number;
  until: string | null;
}
/** The rule as it is kept, from what New event sent and the first day. */
export function seriesRuleOf(repeat: { every: RepeatEvery; n?: number | null; until: string | null } | null | undefined, startKey: string): SeriesRule | null {
  if (!repeat || repeat.every === 'none') return null;
  if (repeat.every === 'biweekly' || repeat.every === 'everyN') return { every: 'everyN', n: repeatWeeks(repeat.every, repeat.n), until: repeat.until };
  if (repeat.every === 'monthlyNth') return { every: 'monthlyNth', nth: nthOfMonth(startKey), until: repeat.until };
  return { every: repeat.every, until: repeat.until };
}

const ORDINALS = { en: ['', '1st', '2nd', '3rd', '4th', '5th'], es: ['', '1.er', '2.º', '3.er', '4.º', '5.º'] } as const;
const SHORT_MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'],
} as const;
const WEEKDAY_WORDS = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  es: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
} as const;

/**
 * A repeat in words, for an event on this day: "Every week" · "Every other week" · "Every 3 weeks" · "Every month
 * on the 2nd Tuesday" · "Every month on the last Thursday". The weekday is the day's own.
 */
export function repeatWords(rule: { every: RepeatEvery; n?: number | null; nth?: number | null }, dayKey: string, lang: 'en' | 'es'): string {
  const es = lang === 'es';
  const wd = isDateKey(dayKey) ? WEEKDAY_WORDS[lang][dayOfWeek(dayKey)]! : '—';
  switch (rule.every) {
    case 'none':
      return es ? 'No se repite' : 'Does not repeat';
    case 'weekly':
      return es ? 'Cada semana' : 'Every week';
    case 'biweekly':
    case 'everyN': {
      const n = repeatWeeks(rule.every, rule.n);
      return n === 2 ? (es ? 'Cada dos semanas' : 'Every other week') : es ? `Cada ${n} semanas` : `Every ${n} weeks`;
    }
    case 'monthlyNth': {
      const nth = ORDINALS[lang][Math.min(5, Math.max(1, rule.nth ?? (isDateKey(dayKey) ? nthOfMonth(dayKey) : 1)))]!;
      return es ? `Cada mes, el ${nth} ${wd}` : `Every month on the ${nth} ${wd}`;
    }
    case 'monthlyLast':
      return es ? `Cada mes, el último ${wd}` : `Every month on the last ${wd}`;
  }
}

/** "Jun 30" / "30 jun" — the end of a repeat, said short (with the year only when it is a year or more after `from`). */
export function shortDay(dayKey: string, lang: 'en' | 'es', from?: string): string {
  const m = SHORT_MONTHS[lang][Number(dayKey.slice(4, 6)) - 1]!;
  const d = Number(dayKey.slice(6, 8));
  const year = from !== undefined && isDateKey(from) && monthOf(dayKey) >= addMonths(monthOf(from), 12) ? dayKey.slice(0, 4) : '';
  return lang === 'es' ? `${d} ${m}${year ? ` ${year}` : ''}` : `${m} ${d}${year ? `, ${year}` : ''}`;
}

/**
 * The summary line, said before the press: "Every month on the last Thursday, until Jun 30 — 9 events". The count
 * is the days the repeat makes, the first included (a day the building is closed is left out and named at the save).
 */
export function repeatSummary(rule: { every: RepeatEvery; n?: number | null; nth?: number | null; until: string | null }, startKey: string, lang: 'en' | 'es'): string {
  const words = repeatWords(rule, startKey, lang);
  if (rule.every === 'none' || !rule.until) return words;
  const count = repeatDays(startKey, rule.every, rule.until, rule.n).length;
  const until = shortDay(rule.until, lang, startKey);
  return lang === 'es' ? `${words}, hasta el ${until} — ${count} ${count === 1 ? 'evento' : 'eventos'}` : `${words}, until ${until} — ${count} ${count === 1 ? 'event' : 'events'}`;
}

/* ------------------------------------------------------------------------------------------------
 * What an event has beside its row
 * ---------------------------------------------------------------------------------------------- */

/** The calendar's part of an event (the event's own file). */
export interface CalendarExtras {
  /** Bingo · Music · … — from the Settings list; '' on an event from before 1.7.7. */
  kind: string;
  /** What to bring ("A sweater — the room is cool"). */
  bring: string;
  /** The accessibility line ("Step-free; large-print cards at the door"). */
  access: string;
  /** D-1028-ONELANG-8: the Spanish wording the office typed beside the English (never made by the app; `_app` file only). */
  bringEs?: string;
  accessEs?: string;
  descriptionEs?: string;
  /** RSVP on or off — most monthly offerings are no RSVP, just the calendar. */
  rsvp: boolean;
  /** The series a Repeat made it part of; null for a single event. Each event stands alone. */
  seriesId: string | null;
  /** 1.7.9: the rule the series was made by (absent on a series from before 1.7.9, and on a single event). */
  repeat?: SeriesRule | null;
  reminders: ReminderChoice;
  /** 25 Oct 2026: In-house · Outing · Raffle · Calendar only, as the office answered it. Absent on an event from before (the facts give it; ./event-kind-1025). */
  eventKind?: EventKind1025 | null;
  /** 25 Oct 2026: wheelchair spots set aside (0–40); absent or null = this event does not count them (./event-wheelchair-1025). */
  wheelchairSpots?: number | null;
  /** 26 Oct 2026 (D-1026-HOUSEHOLD): count the whole household, or limited tickets per household. Absent = what the kind gives. */
  countMode?: 'household' | 'limited' | null;
  /** 26 Oct 2026: N for limited tickets (absent = 2). */
  ticketsPerHousehold?: number | null;
}
export const NO_EXTRAS: CalendarExtras = { kind: '', bring: '', access: '', rsvp: true, seriesId: null, reminders: DEFAULT_REMINDER_CHOICE };

const optText = (max: number) => z.string().trim().max(max).optional().default('');

/** The fields New event gained in 1.7.7 (merged into EventInputSchema, each with a default). */
export const CalendarInputShape = {
  kind: optText(60),
  bring: optText(300),
  accessLine: optText(300),
  /** D-1028-ONELANG-8: the Spanish wording beside the English (kept in the event's file). */
  bringEs: optText(300),
  accessLineEs: optText(300),
  descriptionEs: optText(2000),
  /** RSVP on (the default) or off. */
  rsvp: z.boolean().optional().default(true),
  /** How many volunteers the event needs (0 = none asked for). */
  volunteersNeeded: z.number().int().min(0).max(200).optional().default(0),
  /** 25 Oct 2026: what New event asked first (kept in the event's file). */
  eventKind: z.enum(EVENT_KINDS_1025).optional(),
  /** 25 Oct 2026: wheelchair spots (null / left out = not counted). */
  wheelchairSpots: z.number().int().min(0).max(40).nullable().optional(),
  /** 26 Oct 2026 (D-1026-HOUSEHOLD): how a household is counted on a new event (same keys Edit sends). */
  countMode: z.enum(['household', 'limited']).optional(),
  ticketsPerHousehold: z.number().int().min(1).max(20).nullable().optional(),
  repeat: RepeatSchema.optional(),
  reminders: ReminderChoiceSchema.optional(),
  /**
   * A long Repeat is made a few events a request (each event is a dozen writes): the answer's `series.next` is sent
   * back here, with the same input, until `series.remaining` is 0.
   */
  seriesContinue: z.object({ seriesId: z.string().regex(/^SER-\d{8}-[A-F0-9]{6}$/), after: dayKeySchema }).optional(),
} as const;

const minutesSchema = z.number().int().min(6 * 60).max(23 * 60);

/** Edit an event: only what is given changes. The date, the start or the end moves its reminders. */
export const EventEditSchema = z
  .object({
    name: z.string().trim().min(1, 'required').max(120).optional(),
    dayKey: dayKeySchema.optional(),
    minutes: minutesSchema.optional(),
    endMinutes: minutesSchema.nullable().optional(),
    /** The room (an event in the building). */
    room: z.string().trim().min(1, 'required').max(120).optional(),
    description: z.string().trim().max(2000).optional(),
    kind: z.string().trim().max(60).optional(),
    bring: z.string().trim().max(300).optional(),
    accessLine: z.string().trim().max(300).optional(),
    bringEs: z.string().trim().max(300).optional(),
    accessLineEs: z.string().trim().max(300).optional(),
    descriptionEs: z.string().trim().max(2000).optional(),
    rsvp: z.boolean().optional(),
    volunteersNeeded: z.number().int().min(0).max(200).optional(),
    /** 25 Oct 2026: In-house or Outing (a raffle and a calendar-only event are what their switches say). */
    eventKind: z.enum(EVENT_KINDS_1025).optional(),
    /** 25 Oct 2026: wheelchair spots; null = not counted. */
    wheelchairSpots: z.number().int().min(0).max(40).nullable().optional(),
    /** 26 Oct 2026: how a household is counted, and N for limited tickets. */
    countMode: z.enum(['household', 'limited']).optional(),
    ticketsPerHousehold: z.number().int().min(1).max(20).nullable().optional(),
    reminders: ReminderChoiceSchema.optional(),
    /**
     * 1.7.9 — an event made by a repeat: this event only (the default), or this and the ones after it. Only the day
     * of the week, the time, the place and the title go to the later ones (`SERIES_EDIT_FIELDS`); the rest of the
     * edit stays this event's.
     */
    scope: z.enum(['one', 'following']).optional(),
    /** With `following` and a new day: the day this event was on when the sheet opened (the weekday the later ones leave). */
    scopeFromDayKey: dayKeySchema.optional(),
    /**
     * A series edit goes a few events a request: the answer's `series.next` is sent back here, with the same edit,
     * until `series.remaining` is 0.
     */
    seriesContinue: z.object({ after: z.string().regex(/^EVT-[A-Za-z0-9-]{4,36}$/) }).optional(),
  })
  .refine((e) => e.endMinutes === undefined || e.endMinutes === null || e.minutes === undefined || e.endMinutes > e.minutes, { message: 'after-start', path: ['endMinutes'] });
export type EventEditInput = z.input<typeof EventEditSchema>;

/** What "This and the ones after it" carries to the later events of a series; every other field is this event's alone. */
export const SERIES_EDIT_FIELDS = ['dayKey', 'minutes', 'endMinutes', 'room', 'name'] as const;
/** Does this edit touch something the series question is about (the day of the week, the time, the place, the title)? */
export const touchesSeries = (edit: EventEditInput) => SERIES_EDIT_FIELDS.some((f) => edit[f] !== undefined);

/**
 * Where a later event of a series goes when the edited one changed its weekday — by the rule the series was made by:
 * the last Wednesday of its own month, the 2nd Wednesday of its own month, or (a weekly repeat, and a series from
 * before the rule was kept) the same number of days as the edited one moved. An event that is not on the weekday
 * the series left is one somebody placed by hand, or one already moved: it stays (null).
 */
export function seriesDayFor(rule: SeriesRule | null | undefined, dayKey: string, from: string, to: string): string | null {
  const oldWd = dayOfWeek(from);
  const newWd = dayOfWeek(to);
  if (oldWd === newWd) return null;
  if (rule?.every === 'monthlyLast') return lastWeekdayIn(monthOf(dayKey), newWd);
  if (rule?.every === 'monthlyNth') return nthWeekdayIn(monthOf(dayKey), Math.min(4, Math.max(1, rule.nth ?? nthOfMonth(dayKey))), newWd);
  if (dayOfWeek(dayKey) !== oldWd) return null;
  // the same step the edited event took when it stayed within a week of itself; otherwise the nearest such weekday
  const whole = Math.round((Date.UTC(Number(to.slice(0, 4)), Number(to.slice(4, 6)) - 1, Number(to.slice(6, 8))) - Date.UTC(Number(from.slice(0, 4)), Number(from.slice(4, 6)) - 1, Number(from.slice(6, 8)))) / 86_400_000);
  const near = ((newWd - oldWd + 10) % 7) - 3;
  return addDaysToKey(dayKey, Math.abs(whole) < 7 ? whole : near);
}

/** A series edit's outcome, on the Edit answer. */
export interface SeriesEditResult {
  seriesId: string;
  /** The later events this request changed. */
  changed: { eventId: string; dayKey: string }[];
  /** Later events left as they were, or whose day was left (the building is closed that day) — each with its reason. */
  left: { eventId: string; dayKey: string; why: string }[];
  /** The later events of the series the edit goes to (not cancelled, not past). */
  total: number;
  /** Still to do; above 0, send the same edit again with `seriesContinue: next`. */
  remaining: number;
  next: { after: string } | null;
}

export const CancelEventSchema = z.object({ reason: z.string().trim().min(1, 'required').max(200) });
export type CancelEventInput = z.input<typeof CancelEventSchema>;

/* ------------------------------------------------------------------------------------------------
 * Reminder rows (the event's page)
 * ---------------------------------------------------------------------------------------------- */

export type ReminderRowState = 'scheduled' | 'sent' | 'cancelled' | 'failed' | 'not-sent';

/** "Reminder · Tue 14 Oct 10:00 AM · 128 tenants · scheduled — Cancel". */
export interface ReminderRowView {
  /** Which of the event's reminder times (0 = the earliest). */
  n: number;
  communicationId: string;
  daysBefore: number;
  /** When it leaves (or left) — an instant. */
  at: string;
  state: ReminderRowState;
  audience: ReminderAudience;
  /** How many it would reach now (scheduled) or reached (sent): tenants, and the confirmed volunteers. */
  tenants: number;
  volunteers: number;
  canCancel: boolean;
  /** Why it did not go (cancelled by whom; nobody to send to). */
  note: string | null;
}

/** Why an event has no reminder rows, in a word the page turns into a sentence. */
export type NoRemindersWhy = 'settings-off' | 'event-off' | 'nobody' | 'passed' | 'cancelled' | 'no-to' | null;

export interface EventReminders {
  choice: ReminderChoice;
  /** What applies after Settings and the event's own choice. */
  effective: ReminderSettings;
  rows: ReminderRowView[];
  why: NoRemindersWhy;
}

/* ------------------------------------------------------------------------------------------------
 * Volunteers on an event
 * ---------------------------------------------------------------------------------------------- */

export interface HelperLine {
  rsvpId: string;
  /** Empty on a staff line (no volunteer record) — the line's rsvpId is its key. */
  volunteerId: string;
  /** 1 Nov 2026 (D-1102-STAFF): a staff member or colleague; counted in "4 of 6", never sent a request. */
  staff?: boolean;
  /** First name on the page's count line; the full name in the list. */
  firstName: string;
  name: string;
  /** Added by the office's hand, with its reason. */
  byHand: string | null;
}

/** "4 of 6 volunteers · Ana, Jun, Priya, Tomas". */
export interface EventHelpers {
  needed: number;
  going: HelperLine[];
  waiting: HelperLine[];
  /** Said "Not this time". */
  no: number;
  /** Asked and not answered yet. */
  noAnswer: number;
  /** When Request volunteers was last pressed (this event alone, or the month's request). */
  requestedAt: string | null;
  /** 24 Oct 2026: the volunteer ids already thanked for this event (Thank the helpers). An older answer of the server has none. */
  thanked?: string[];
  /** Ready volunteers the request would reach now who do not have it yet. */
  toAsk: number;
  /** 1.8.3: the standby list in the order joined, the places open, and who Offer to the next would offer to (an event with a number; an older answer of the server has none). */
  standby?: StandbyView;
}

/** The line on a volunteer's card: "Events: available for 3 · not available 1 · no answer 2" — this month. */
export interface VolunteerMonthLine {
  month: string;
  available: number;
  notAvailable: number;
  noAnswer: number;
}

/* ------------------------------------------------------------------------------------------------
 * The month
 * ---------------------------------------------------------------------------------------------- */

/** What the calendar needs of Settings (kinds, rooms, the reminder defaults). */
export interface CalendarSettings {
  kinds: string[];
  rooms: string[];
  reminders: ReminderSettings;
}

/** One line of the month, as the announcement and the printed page list it: day · time · event · place. */
export interface MonthLine {
  eventId: string;
  dayKey: string;
  minutes: number | null;
  endMinutes: number | null;
  name: string;
  kind: string;
  place: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** 14:00 → "2:00 PM" (the calendar's own clock; the same in both languages). */
export function clock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  return `${((h + 11) % 12) + 1}:${pad(minutes % 60)} ${h < 12 ? 'AM' : 'PM'}`;
}

const MONTHS = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  es: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
} as const;
const WEEKDAYS = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  es: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
} as const;
export const monthName = (month: string, lang: 'en' | 'es') => `${MONTHS[lang][Number(month.slice(4, 6)) - 1]} ${month.slice(0, 4)}`;
/** "Tuesday, October 14" / "martes 14 de octubre". */
export function dayLine(dayKey: string, lang: 'en' | 'es'): string {
  const wd = WEEKDAYS[lang][dayOfWeek(dayKey)]!;
  const m = MONTHS[lang][Number(dayKey.slice(4, 6)) - 1]!;
  const d = Number(dayKey.slice(6, 8));
  return lang === 'es' ? `${wd} ${d} de ${m}` : `${wd}, ${m} ${d}`;
}

export const sortMonthLines = <T extends Pick<MonthLine, 'dayKey' | 'minutes' | 'name'>>(lines: readonly T[]): T[] => [...lines].sort((a, b) => a.dayKey.localeCompare(b.dayKey) || (a.minutes ?? 0) - (b.minutes ?? 0) || a.name.localeCompare(b.name));

/** One line as it is written: "Tuesday, October 14 · 2:00 PM · Bingo night · Auditorium". */
export function monthLineText(l: MonthLine, lang: 'en' | 'es'): string {
  return [dayLine(l.dayKey, lang), l.minutes === null ? '' : clock(l.minutes), l.name, l.place].filter(Boolean).join(' · ');
}

const ANNOUNCE_WORDS = {
  en: { subject: (m: string) => `${m} at Selis Manor`, intro: (m: string) => `Here is what is happening at Selis Manor in ${m}. Everyone is welcome.`, none: 'Nothing is on the calendar yet.', outro: 'Questions? Call the office.' },
  es: { subject: (m: string) => `${m} en Selis Manor`, intro: (m: string) => `Esto es lo que habrá en Selis Manor en ${m}. Todos son bienvenidos.`, none: 'Todavía no hay nada en el calendario.', outro: '¿Preguntas? Llame a la oficina.' },
} as const;
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Announce the month": the announcement's two boxes, the calendar as a clean list. A person reads it and presses Send. */
export function monthAnnouncement(month: string, lines: readonly MonthLine[]): { subject: { en: string; es: string }; body: { en: string; es: string } } {
  const make = (lang: 'en' | 'es') => {
    const w = ANNOUNCE_WORDS[lang];
    const name = monthName(month, lang);
    const list = sortMonthLines(lines).map((l) => `- ${monthLineText(l, lang)}`);
    return { subject: w.subject(capital(name)), body: [w.intro(name), list.length ? list.join('\n') : w.none, w.outro].join('\n\n') };
  };
  const en = make('en');
  const es = make('es');
  return { subject: { en: en.subject, es: es.subject }, body: { en: en.body, es: es.body } };
}

/* ------------------------------------------------------------------------------------------------
 * Copy last month
 * ---------------------------------------------------------------------------------------------- */

/** One proposed event: last month's, on the same weekday of this month. Nothing is saved until the office says. */
export interface EventProposal {
  /** The event it was copied from. */
  fromEventId: string;
  fromDayKey: string;
  name: string;
  kind: string;
  /** The proposed day — the same nth weekday; null when this month has no such day (the office picks one). */
  dayKey: string | null;
  minutes: number;
  endMinutes: number | null;
  room: string;
  description: string;
  bring: string;
  accessLine: string;
  rsvp: boolean;
  volunteersNeeded: number;
  /** Why the proposed day needs a look (the building is closed; no such weekday; an event of that name is already there). */
  note: 'closed' | 'no-such-day' | 'already' | 'passed' | null;
}

export interface CopySource {
  eventId: string;
  dayKey: string;
  name: string;
  kind: string;
  minutes: number | null;
  endMinutes: number | null;
  room: string;
  description: string;
  bring: string;
  accessLine: string;
  rsvp: boolean;
  volunteersNeeded: number;
}

/**
 * This month from last month's kinds and weekdays (the same way housekeeping proposes): the 2nd Tuesday's bingo is
 * proposed for this month's 2nd Tuesday. Pure — the caller saves nothing; the office edits and saves what it keeps.
 */
export function copyLastMonth(last: readonly CopySource[], month: string, o: { todayKey: string; closed?: ReadonlySet<string>; existing?: readonly { dayKey: string; name: string }[] }): EventProposal[] {
  const have = new Set((o.existing ?? []).map((e) => `${e.dayKey}|${e.name.trim().toLowerCase()}`));
  return sortMonthLines(last.map((e) => ({ ...e, place: e.room }))).map((e) => {
    const dayKey = nthWeekdayIn(month, nthOfMonth(e.dayKey), dayOfWeek(e.dayKey));
    const note: EventProposal['note'] = !dayKey ? 'no-such-day' : have.has(`${dayKey}|${e.name.trim().toLowerCase()}`) ? 'already' : dayKey < o.todayKey ? 'passed' : o.closed?.has(dayKey) ? 'closed' : null;
    return { fromEventId: e.eventId, fromDayKey: e.dayKey, name: e.name, kind: e.kind, dayKey, minutes: e.minutes ?? 14 * 60, endMinutes: e.endMinutes, room: e.room, description: e.description, bring: e.bring, accessLine: e.accessLine, rsvp: e.rsvp, volunteersNeeded: e.volunteersNeeded, note };
  });
}

export interface CopyLastMonthResponse {
  month: string;
  fromMonth: string;
  proposals: EventProposal[];
}

/** Save what the office kept of a proposal (or any list of new events): each is made alone; one refusal stops only itself. */
export interface BatchResult {
  created: { eventId: string; name: string; dayKey: string }[];
  skipped: { name: string; dayKey: string; why: string }[];
  message: string;
}

/** A Repeat's outcome, on the New event answer. */
export interface SeriesResult {
  seriesId: string;
  /** Made by this request (the app adds the requests up). */
  created: { eventId: string; dayKey: string }[];
  skipped: { dayKey: string; why: string }[];
  /** Every day of the repeat, the first included. */
  total: number;
  /** Days still to make; above 0, send the same input again with `seriesContinue: next`. */
  remaining: number;
  next: { seriesId: string; after: string } | null;
}
/** How many events of a Repeat one request makes (the app presses on until none remain). */
export const SERIES_PART = 3;

/* ------------------------------------------------------------------------------------------------
 * The month's request to volunteers (one email, one page, an answer an event)
 * ---------------------------------------------------------------------------------------------- */

export type HelpAnswer = 'yes' | 'no';
/** Where a volunteer stands on one event that needs volunteers. */
export type HelpState = 'none' | 'going' | 'waiting' | 'no';

export interface VolunteerMonthEvent {
  eventId: string;
  name: string;
  dayKey: string;
  minutes: number | null;
  endMinutes: number | null;
  place: string;
  needed: number;
  going: number;
  /** We have enough: a new yes joins the waiting list. */
  full: boolean;
  mine: HelpState;
  /** Passed, cancelled or closed: shown, not answerable. */
  open: boolean;
}

/** The volunteer's month page (`/link/<token>`, kind `volunteer-month`). Opening it never acts. */
export interface VolunteerMonthPage {
  state: 'ok' | 'not-valid' | 'expired' | 'replaced' | 'opted-out';
  lang: 'en' | 'es';
  firstName: string;
  month: string;
  events: VolunteerMonthEvent[];
  officePhone: string | null;
}

export const VolunteerMonthAnswerSchema = z.object({ eventId: z.string().trim().regex(/^EVT-[A-Za-z0-9-]{4,36}$/), a: z.enum(['yes', 'no']), /** 1.8.3: "Yes, add me" to the standby list (with a yes, after the number is reached). */ standby: z.boolean().optional() });
export interface VolunteerMonthAction {
  ok: boolean;
  message: string;
  /** 1.8.3: the number is reached on this event — the card asks the standby question (`message` is the question); nothing was saved. */
  ask?: string;
  page?: VolunteerMonthPage;
}

/** Before the month's request is pressed: what it lists and whom it reaches. */
export interface MonthRequestPreview {
  month: string;
  events: { eventId: string; name: string; dayKey: string; needed: number; going: number }[];
  volunteers: number;
  noEmail: number;
  optedOut: number;
}

/** The buttons a volunteer gets. Both open their own page at that answer; the answer is sent from the page — a GET never acts. */
export const HELP_BUTTONS = {
  en: { yes: 'I’m available', no: 'Not this time', month: 'Choose the events I can help with' },
  es: { yes: 'Estoy disponible', no: 'Esta vez no', month: 'Elegir los eventos en los que puedo ayudar' },
} as const;

/** What the volunteer reads when the event has the volunteers it needs. */
export const ENOUGH_WORDS = {
  en: 'We have enough — thank you; we’ll let you know if that changes.',
  es: 'Ya tenemos suficientes voluntarios. Gracias; le avisaremos si eso cambia.',
} as const;

/** The day-of reminder on an event with RSVP where something is served says who to expect. */
export const EXPECT_WORDS = {
  en: { seat: (time: string, place: string) => `We’re expecting you${time ? ` at ${time}` : ''}${place ? ` in ${place}` : ''}.`, upstairs: 'Your plate will come up to you.' },
  es: { seat: (time: string, place: string) => `Le esperamos${time ? ` a las ${time}` : ''}${place ? ` en ${place}` : ''}.`, upstairs: 'Le subiremos su plato.' },
} as const;
